/** Share artefacts (M17, §1.8). Split from the api.ts monolith 2026-08-03; the barrel re-exports. */
import {
  type RawError,
  WEB_ORIGIN,
  bareHandle,
  call,
  get,
  num,
  optTextOf,
  post,
  textOf,
} from "./core";
// --- share artefacts (M17, PRD FR-1.8 / §1.8) -------------------------------

/**
 * The virality loops (§1.8). Two of the four artefacts are private and minted
 * per knock — the standing flex (loop 1) and the receipt (loop 3) — so their
 * URLs carry a signed token and arrive ABSOLUTE, built by the server against its
 * own `SITE_ORIGIN`. They are used verbatim and never rebuilt here: the token is
 * the authorisation, and a URL we assembled ourselves would not carry one.
 *
 * The other two are public and addressed by handle — the door-stats card (loop
 * 2) and the House Rules card (loop 4). Those have no minting step and no token,
 * so their URLs are composed here, against THIS origin: it is where the pages and
 * the images are served from (ruling R-0926-1 (1)), and a URL that is posted
 * has to be absolute — the api's relative base would copy as `/s/door/maya`.
 */

export type ShareKind = "standing" | "receipt";

export type ShareLink = {
  kind: ShareKind;
  knock_id: string;
  /** The page to post — absolute, token-carrying, the server's own. */
  share_url: string;
  /** The OG image behind it — absolute, token-carrying, the server's own. */
  og_image_url: string;
  expires_at: string | null;
};

/**
 * `STANDING_NOT_SHAREABLE` is the ordinary answer for most knocks: only the top
 * decile is mintable (§1.8 loop 1). So it is a refusal the screen answers by
 * showing nothing at all — never by explaining what was withheld, which would be
 * teasing somebody with their own standing.
 */
export type ShareFailureCode =
  | "UNAUTHENTICATED"
  | "VALIDATION_ERROR"
  | "KNOCK_NOT_FOUND"
  | "STANDING_NOT_READY"
  | "STANDING_NOT_SHAREABLE"
  | "RECEIPT_NOT_READY"
  | "UNKNOWN";

export type ShareLinkResult = { ok: true; link: ShareLink } | { ok: false; code: ShareFailureCode };

const SHARE_CODES: readonly ShareFailureCode[] = [
  "VALIDATION_ERROR",
  "KNOCK_NOT_FOUND",
  "STANDING_NOT_READY",
  "STANDING_NOT_SHAREABLE",
  "RECEIPT_NOT_READY",
];

export async function createShareLink(kind: ShareKind, knockId: string): Promise<ShareLinkResult> {
  const r = await call(() => post("/api/v1/share/link", { kind, knock_id: knockId }));
  if (!r) return { ok: false, code: "UNKNOWN" };

  if (!r.res.ok) {
    if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
    const code = (r.body as RawError).error?.code;
    const known = SHARE_CODES.find((c) => c === code);
    if (known) return { ok: false, code: known };
    if (r.res.status === 404) return { ok: false, code: "KNOCK_NOT_FOUND" };
    return { ok: false, code: "UNKNOWN" };
  }

  const b = r.body as Record<string, unknown>;
  const shareUrl = textOf(b["share_url"]);
  const imageUrl = textOf(b["og_image_url"]);
  // A link with nothing to post is not a link. Refusing here is what keeps the
  // preview row from rendering a broken image next to a copy button.
  if (!shareUrl || !imageUrl) return { ok: false, code: "UNKNOWN" };

  return {
    ok: true,
    link: {
      kind: b["kind"] === "receipt" ? "receipt" : kind,
      knock_id: typeof b["knock_id"] === "string" && b["knock_id"] ? b["knock_id"] : knockId,
      share_url: shareUrl,
      og_image_url: imageUrl,
      expires_at: optTextOf(b["expires_at"]),
    },
  };
}

/** The posted page for the door-stats card (§1.8 loop 2). Public, no token. */
export function doorStatsShareUrl(handle: string): string {
  return `${WEB_ORIGIN}/s/door/${encodeURIComponent(bareHandle(handle))}`;
}

export function doorStatsImageUrl(handle: string): string {
  return `${WEB_ORIGIN}/og/door/${encodeURIComponent(bareHandle(handle))}.png`;
}

/** The posted page for the House Rules card (§1.8 loop 4). Public, no token. */
export function rulesShareUrl(handle: string): string {
  return `${WEB_ORIGIN}/s/rules/${encodeURIComponent(bareHandle(handle))}`;
}

export function rulesImageUrl(handle: string): string {
  return `${WEB_ORIGIN}/og/rules/${encodeURIComponent(bareHandle(handle))}.png`;
}

/** The public status object behind loop 2 — counts only, never knock content. */
export type DoorStats = {
  handle: string;
  display_name: string;
  knocks_total: number;
  replies: number;
  /** Share of screened knocks that made a Morning Nob, 0–1. */
  hardest_rate: number;
  period: string;
};

export type DoorStatsResult =
  { ok: true; stats: DoorStats } | { ok: false; code: "DOOR_NOT_FOUND" | "UNKNOWN" };

export async function fetchDoorStats(handle: string): Promise<DoorStatsResult> {
  const bare = bareHandle(handle);
  const r = await call(() => get(`/api/v1/door/${encodeURIComponent(bare)}/stats`));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return { ok: false, code: r.res.status === 404 ? "DOOR_NOT_FOUND" : "UNKNOWN" };
  const b = r.body as Record<string, unknown>;
  const wire = typeof b["handle"] === "string" && b["handle"] ? bareHandle(b["handle"]) : bare;
  return {
    ok: true,
    stats: {
      handle: wire,
      display_name:
        typeof b["display_name"] === "string" && b["display_name"].trim()
          ? (b["display_name"] as string)
          : `@${wire}`,
      knocks_total: num(b["knocks_total"]),
      replies: num(b["replies"]),
      hardest_rate: num(b["hardest_rate"]),
      period: typeof b["period"] === "string" && b["period"] ? b["period"] : "all time",
    },
  };
}

/**
 * There is no findability call here any more (ruling R-0825-1, 2026-08-25).
 * Every door is public and searchable, so there is no setting to read and none
 * to write — `GET`/`PATCH /api/v1/door/directory` are gone from the server too.
 */
